import React, { useRef, useMemo, useEffect, useCallback, useState } from 'react';
import { VolumeX, Volume2, Play, Pause, SkipForward, SkipBack } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '../../utils/tokenStorage';

// Extract YouTube video ID from various URL formats
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/,
    /youtube\.com\/live\/([^"&?\/\s]{11})/,
    /^([^"&?\/\s]{11})$/
  ];
  
  for (const regex of patterns) {
    const match = url.match(regex);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  return null;
};

const YouTubePlayer = ({ 
  streamUrl, 
  streamStartedAt, 
  muted, 
  setMuted, 
  isMobile,
  shouldShowVideo,
  youtubeRef,
  socketRef,
  onAudioData,  // Callback for audio analysis data
  volume = 0.7,  // 0-1 volume level
  userRole = 'user'  // For showing admin controls
}) => {
  const localRef = useRef(null);
  const ref = youtubeRef || localRef;
  const animationFrameRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [minimized, setMinimized] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const hasUnmutedRef = useRef(false);
  const isAdmin = ['admin', 'owner', 'overseer'].includes(userRole);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // 1s tick for the LIVE elapsed timer
  useEffect(() => {
    if (!shouldShowVideo || !streamStartedAt) return;
    const iv = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [shouldShowVideo, streamStartedAt]);

  const elapsedLabel = useMemo(() => {
    if (!streamStartedAt) return null;
    const start = new Date(streamStartedAt).getTime();
    if (isNaN(start)) return null;
    let s = Math.max(0, Math.floor((nowTick - start) / 1000));
    const h = Math.floor(s / 3600); s -= h * 3600;
    const m = Math.floor(s / 60); s -= m * 60;
    return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
  }, [streamStartedAt, nowTick]);
  
  const currentVideoId = useMemo(() => getYouTubeVideoId(streamUrl), [streamUrl]);
  
  // Simulated audio visualization based on time
  // Since YouTube iframes don't allow direct audio access due to CORS,
  // we create dynamic "music-like" patterns that sync with time
  const simulateAudioData = useCallback(() => {
    if (!currentVideoId || !onAudioData || !shouldShowVideo) {
      animationFrameRef.current = requestAnimationFrame(simulateAudioData);
      return;
    }
    
    const time = Date.now() / 1000;
    
    // Create realistic-looking audio patterns using multiple sine waves
    // These simulate bass, mids, and treble frequency bands
    
    // Bass - slower, powerful beats (simulating kick drums, bass drops)
    const bassBase = Math.sin(time * 2.5) * 0.5 + 0.5;
    const bassHit = Math.pow(Math.sin(time * 1.2), 8) * 0.8;
    const bass = Math.min(1, bassBase * 0.4 + bassHit + Math.random() * 0.1);
    
    // Mids - medium frequency content (melodies, vocals)
    const mids = (
      Math.sin(time * 4.3) * 0.3 +
      Math.sin(time * 6.7) * 0.2 +
      Math.sin(time * 3.1) * 0.2 +
      0.5
    ) * 0.7 + Math.random() * 0.05;
    
    // Treble - high frequency sparkle (hi-hats, cymbals)
    const treble = (
      Math.sin(time * 12) * 0.3 +
      Math.sin(time * 8.5) * 0.2 +
      Math.random() * 0.3
    ) * 0.6 + 0.2;
    
    // Overall level
    const overall = (bass * 0.5 + mids * 0.3 + treble * 0.2);
    
    // Peak for strobe/flash effects
    const peak = Math.max(bass, mids, treble);
    
    onAudioData({
      bass: Math.min(1, Math.max(0, bass)),
      mids: Math.min(1, Math.max(0, mids)),
      treble: Math.min(1, Math.max(0, treble)),
      overall: Math.min(1, Math.max(0, overall)),
      peak: Math.min(1, Math.max(0, peak))
    });
    
    animationFrameRef.current = requestAnimationFrame(simulateAudioData);
  }, [currentVideoId, onAudioData, shouldShowVideo]);
  
  // Start simulation when video is showing
  useEffect(() => {
    if (currentVideoId && onAudioData && shouldShowVideo) {
      animationFrameRef.current = requestAnimationFrame(simulateAudioData);
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [currentVideoId, simulateAudioData, onAudioData, shouldShowVideo]);
  
  // Sync volume to YouTube iframe when volume prop changes
  useEffect(() => {
    if (ref.current?.contentWindow && currentVideoId) {
      ref.current.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'setVolume', args: [Math.round(volume * 100)] }),
        '*'
      );
    }
  }, [volume, currentVideoId]);
  
  // Auto-unmute after user interaction (browsers require muted autoplay)
  useEffect(() => {
    if (hasUnmutedRef.current) return;
    const handleInteraction = () => {
      if (!hasUnmutedRef.current && ref.current?.contentWindow) {
        ref.current.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'unMute' }), '*'
        );
        ref.current.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'setVolume', args: [Math.round(volume * 100)] }), '*'
        );
        hasUnmutedRef.current = true;
        setMuted(false);
      }
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
    document.addEventListener('click', handleInteraction);
    document.addEventListener('keydown', handleInteraction);
    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
  }, [currentVideoId, volume, setMuted]);
  
  // Listen for video control commands from server (sync across all clients)
  useEffect(() => {
    if (!socketRef?.current) return;
    
    const handleVideoControl = (data) => {
      if (!ref.current?.contentWindow) return;
      
      switch (data.action) {
        case 'play':
          ref.current.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'playVideo' }),
            '*'
          );
          setIsPlaying(true);
          break;
        case 'pause':
          ref.current.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'pauseVideo' }),
            '*'
          );
          setIsPlaying(false);
          break;
        case 'seek':
          ref.current.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'seekTo', args: [data.time, true] }),
            '*'
          );
          break;
        case 'skip':
          // Get current time and skip
          ref.current.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'getCurrentTime' }),
            '*'
          );
          // The actual skip will be handled in the message listener
          pendingSkipRef.current = data.seconds;
          break;
        default:
          break;
      }
    };
    
    socketRef.current.on('video_control', handleVideoControl);
    
    return () => {
      socketRef.current?.off('video_control', handleVideoControl);
    };
  }, [socketRef]);
  
  const pendingSkipRef = useRef(null);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);
  
  // Handshake with the YouTube iframe API so state/error events actually flow
  const sendListening = useCallback(() => {
    try {
      ref.current?.contentWindow?.postMessage(
        JSON.stringify({ event: 'listening', id: 'youtube-player', channel: 'widget' }), '*'
      );
    } catch (e) { /* noop */ }
  }, [ref]);

  const erroredRef = useRef(null);

  // Listen for YouTube API postMessage events (video end, state changes, time)
  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.data || typeof event.data !== 'string') return;
      try {
        const data = JSON.parse(event.data);
        // Embedded playback failed (blocked video, region lock, stream 403) — auto-advance
        if (data.event === 'onError' && erroredRef.current !== currentVideoId) {
          erroredRef.current = currentVideoId;
          toast.error("That track can't play in the embedded player — skipping to the next one 🎵");
          if (socketRef?.current?.connected) {
            socketRef.current.emit('video_ended', { video_id: currentVideoId, reason: 'embed_error' });
          }
          return;
        }
        // YouTube player state: 0 = ended, 1 = playing, 2 = paused
        if (data.event === 'onStateChange') {
          if (data.info === 0) {
            // Video ended - notify backend to auto-advance
            if (socketRef?.current?.connected) {
              socketRef.current.emit('video_ended', { video_id: currentVideoId });
            }
            setIsPlaying(false);
          } else if (data.info === 1) {
            setIsPlaying(true);
          } else if (data.info === 2) {
            setIsPlaying(false);
          }
        }
        // Handle current time response for skip
        if (data.event === 'infoDelivery' && data.info?.currentTime !== undefined && pendingSkipRef.current !== null) {
          const newTime = Math.max(0, data.info.currentTime + pendingSkipRef.current);
          ref.current?.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: 'seekTo', args: [newTime, true] }),
            '*'
          );
          pendingSkipRef.current = null;
        }
      } catch (e) {
        // Not a JSON message from YouTube, ignore
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [currentVideoId, socketRef]);
  
  // Admin control functions - send both local postMessage AND server API for sync
  const handlePlay = async () => {
    // Immediate local play via postMessage
    if (ref.current?.contentWindow) {
      ref.current.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'playVideo' }), '*'
      );
    }
    setIsPlaying(true);
    
    // Also notify server to sync all other clients
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/nightclub/video/play`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) {
      console.error('Play sync error:', e);
    }
  };
  
  const handlePause = async () => {
    // Immediate local pause via postMessage
    if (ref.current?.contentWindow) {
      ref.current.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo' }), '*'
      );
    }
    setIsPlaying(false);
    
    // Also notify server to sync all other clients
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/nightclub/video/pause`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) {
      console.error('Pause sync error:', e);
    }
  };
  
  const handleSkip = async (seconds) => {
    // Immediate local skip via postMessage
    if (ref.current?.contentWindow) {
      ref.current.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'getCurrentTime' }), '*'
      );
      pendingSkipRef.current = seconds;
    }
    
    // Also notify server to sync all other clients
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/nightclub/video/skip`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ seconds })
      });
    } catch (e) {
      console.error('Skip sync error:', e);
    }
  };
  
  if (!streamUrl || !currentVideoId) {
    // Show debug info for invalid URL
    if (streamUrl && shouldShowVideo) {
      return (
        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-30 bg-red-900/90 px-4 py-2 rounded-lg text-white text-xs">
          <p className="font-bold">Invalid YouTube URL</p>
          <p className="text-red-300 truncate max-w-xs">{streamUrl}</p>
        </div>
      );
    }
    return null;
  }
  
  return (
    <div
      className="absolute transition-all duration-300"
      data-testid="youtube-video-container"
      style={{
        left: '50%',
        transform: 'translateX(-50%)',
        top: isMobile ? '8px' : '20px',
        width: minimized ? '150px' : (isMobile ? '200px' : '280px'),
        height: minimized ? '28px' : (isMobile ? '112px' : '160px'),
        zIndex: shouldShowVideo ? 25 : -1,
        overflow: 'hidden',
        borderRadius: minimized ? '9999px' : '8px',
        border: minimized ? '2px solid #DC2626' : '4px solid #DC2626',
        boxShadow: minimized ? '0 0 16px rgba(220,38,38,0.5)' : '0 0 40px rgba(220, 38, 38, 0.8), 0 0 60px rgba(147, 51, 234, 0.4)',
        background: '#000',
        opacity: shouldShowVideo ? 1 : 0,
        pointerEvents: shouldShowVideo ? 'auto' : 'none',
      }}
    >
      <iframe
        ref={ref}
        key={`yt-${currentVideoId}`}
        id="youtube-player"
        src={`https://www.youtube.com/embed/${currentVideoId}?autoplay=1&mute=1&controls=0&disablekb=1&fs=0&iv_load_policy=3&modestbranding=1&rel=0&playsinline=1&enablejsapi=1&origin=${window.location.origin}`}
        allow="autoplay; encrypted-media; accelerometer; gyroscope; picture-in-picture; fullscreen; autoplay"
        allowFullScreen
        title="Live Stream"
        onLoad={sendListening}
        style={{
          width: isMobile ? '200px' : '280px',
          height: isMobile ? '112px' : '160px',
          border: 'none',
          display: 'block',
          opacity: minimized ? 0 : 1,
        }}
      />

      {/* Minimized pill — audio keeps playing, tap to expand */}
      {minimized && shouldShowVideo && (
        <div
          className="absolute inset-0 z-[8] flex items-center justify-center gap-1.5 cursor-pointer bg-black/90"
          data-testid="youtube-minimized-pill"
          onClick={(e) => { e.stopPropagation(); setMinimized(false); }}
        >
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-white text-[10px] font-bold">LIVE{elapsedLabel ? ` • ${elapsedLabel}` : ''}</span>
          <span className="text-white/50 text-[9px]">tap to expand</span>
        </div>
      )}

      {/* Click shield — keeps taps inside the club instead of jumping to YouTube */}
      {shouldShowVideo && !minimized && (
        <div
          className="absolute inset-0 z-[5]"
          data-testid="youtube-click-shield"
          onClick={(e) => {
            e.stopPropagation();
            if (ref.current?.contentWindow) {
              ref.current.contentWindow.postMessage(
                JSON.stringify({ event: 'command', func: muted ? 'unMute' : 'mute' }), '*'
              );
            }
            setMuted(!muted);
            toast.success(muted ? 'Sound ON!' : 'Sound OFF');
          }}
        />
      )}
      
      {/* LIVE badge + elapsed time */}
      {shouldShowVideo && !minimized && (
        <div className="absolute top-1 left-1 z-10 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/70" data-testid="live-badge">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          <span className="text-white text-[9px] font-bold">LIVE{elapsedLabel ? ` ${elapsedLabel}` : ''}</span>
        </div>
      )}

      {/* Minimize button */}
      {shouldShowVideo && !minimized && (
        <button
          className="absolute top-1 right-1 z-10 w-5 h-5 rounded-full bg-black/70 hover:bg-black/90 text-white/70 hover:text-white text-[10px] leading-none flex items-center justify-center"
          data-testid="youtube-minimize-btn"
          title="Minimize video (audio keeps playing)"
          onClick={(e) => { e.stopPropagation(); setMinimized(true); }}
        >
          —
        </button>
      )}

      {/* Admin sync controls — play/pause/skip for everyone in the club */}
      {shouldShowVideo && !minimized && isAdmin && (
        <div className="absolute bottom-1 left-1 z-10 flex items-center gap-1" data-testid="admin-video-controls">
          <button
            className="w-6 h-6 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center"
            data-testid="video-skip-back-btn"
            title="Back 10s (syncs everyone)"
            onClick={(e) => { e.stopPropagation(); handleSkip(-10); }}
          >
            <SkipBack className="w-3 h-3" />
          </button>
          <button
            className="w-6 h-6 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center"
            data-testid="video-play-pause-btn"
            title={isPlaying ? 'Pause for everyone' : 'Play for everyone'}
            onClick={(e) => { e.stopPropagation(); isPlaying ? handlePause() : handlePlay(); }}
          >
            {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
          </button>
          <button
            className="w-6 h-6 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center"
            data-testid="video-skip-fwd-btn"
            title="Forward 10s (syncs everyone)"
            onClick={(e) => { e.stopPropagation(); handleSkip(10); }}
          >
            <SkipForward className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Sound toggle button - always visible */}
      {shouldShowVideo && !minimized && (
        <div 
          className="absolute bottom-1 right-1 cursor-pointer z-10"
          onClick={(e) => {
            e.stopPropagation();
            if (ref.current) {
              ref.current.contentWindow.postMessage(
                JSON.stringify({ event: 'command', func: muted ? 'unMute' : 'mute' }), 
                '*'
              );
            }
            setMuted(!muted);
            toast.success(muted ? 'Sound ON!' : 'Sound OFF');
          }}
        >
          <div className={`px-2 py-1 rounded-full flex items-center gap-1 text-white text-xs shadow-lg transition-all ${
            muted ? 'bg-red-600/90 hover:bg-red-500' : 'bg-green-600/90 hover:bg-green-500'
          }`}>
            {muted ? (
              <>
                <VolumeX className="w-3 h-3" />
                <span>OFF</span>
              </>
            ) : (
              <>
                <Volume2 className="w-3 h-3" />
                <span>ON</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export { YouTubePlayer, getYouTubeVideoId };
export default YouTubePlayer;
